import { useMemo, useState, type FormEvent } from "react";
import { WeightChart } from "../components/WeightChart";
import type { AppController, AppSnapshot, DaySummary, ExecuteAction } from "../types";

interface WeightViewProps {
  snapshot: AppSnapshot;
  controller: AppController;
  execute: ExecuteAction;
}

export function WeightView({ snapshot, controller, execute }: WeightViewProps) {
  const allowedDays = useMemo(() => getWeightDays(snapshot), [snapshot]);
  const [date, setDate] = useState(() => allowedDays.find((day) => day.isToday)?.date ?? allowedDays.at(-1)?.date ?? snapshot.todayDate);
  const [weight, setWeight] = useState("");
  const [saving, setSaving] = useState(false);
  const { unit } = snapshot.weight;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const entered = Number(weight);
    if (!Number.isFinite(entered) || entered <= 0) return;
    const weightKg = unit === "lb" ? entered / 2.2046226218 : entered;
    setSaving(true);
    const result = await execute(() => controller.saveWeight({ date, weightKg }), { successMessage: "Weight saved." });
    if (result.ok) setWeight("");
    setSaving(false);
  }

  return (
    <section className="page-section" aria-labelledby="weight-heading">
      <header className="page-heading">
        <div><p className="eyebrow">Progress, not perfection</p><h1 id="weight-heading">Weight</h1></div>
        {snapshot.weight.latest !== undefined && (
          <div className="header-metric"><strong>{snapshot.weight.latest.toFixed(1)}</strong><span>{unit} latest</span></div>
        )}
      </header>

      {snapshot.checkIn.isLoggingLocked && (
        <div className="notice notice--warning" role="status">
          <div><strong>New logging is paused</strong><p>You may still backfill the closing week or record today’s check-in measurement.</p></div>
        </div>
      )}

      <article className="card weight-entry-card">
        <div className="card__heading"><div><h2>Log a weight</h2><p>Best measured in the morning before eating or drinking, after using the toilet.</p></div></div>
        <form className="weight-entry-form" onSubmit={submit}>
          <label className="field">
            <span>Date</span>
            <select value={date} onChange={(event) => setDate(event.target.value)} disabled={allowedDays.length === 0}>
              {allowedDays.map((day) => <option key={day.date} value={day.date}>{day.longLabel}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Weight</span>
            <span className="input-with-suffix input-with-suffix--large">
              <input
                type="number"
                inputMode="decimal"
                min="20"
                max="500"
                step="0.1"
                required
                placeholder={unit === "kg" ? "80.0" : "176.4"}
                value={weight}
                onChange={(event) => setWeight(event.target.value)}
                disabled={allowedDays.length === 0}
              />
              <i>{unit}</i>
            </span>
          </label>
          <button className="button button--primary" type="submit" disabled={saving || !weight || allowedDays.length === 0}>
            {saving ? "Saving…" : "Save weight"}
          </button>
        </form>
      </article>

      <article className="card chart-card">
        <div className="card__heading">
          <div><h2>Weight trend</h2><p>The average uses available weights from each day and the four calendar days before it.</p></div>
          {snapshot.weight.rollingAverage !== undefined && (
            <div className="header-metric header-metric--green"><strong>{snapshot.weight.rollingAverage.toFixed(1)}</strong><span>{unit} trend</span></div>
          )}
        </div>
        <WeightChart points={snapshot.weight.points} unit={unit} />
        {snapshot.weight.rollingAverage !== undefined && snapshot.weight.rollingAverageSampleSize < 5 && (
          <p className="field-hint">Current trend uses {snapshot.weight.rollingAverageSampleSize} available {snapshot.weight.rollingAverageSampleSize === 1 ? "weight" : "weights"} from the last five calendar days.</p>
        )}
      </article>
    </section>
  );
}

type WeightDay = Pick<DaySummary, "date" | "longLabel" | "isToday">;

function getWeightDays(snapshot: AppSnapshot): WeightDay[] {
  if (!snapshot.checkIn.isLoggingLocked) return snapshot.week.days.filter((day) => !day.isFuture);
  const allowed = new Set(snapshot.checkIn.allowedBackfillDates);
  const days: WeightDay[] = snapshot.week.days.filter((day) => allowed.has(day.date));
  if (
    (snapshot.checkIn.status === "due" || snapshot.checkIn.status === "overdue") &&
    !days.some((day) => day.date === snapshot.todayDate)
  ) {
    days.push({
      date: snapshot.todayDate,
      longLabel: `${formatWeightDate(snapshot.todayDate)} — today’s check-in measurement`,
      isToday: true,
    });
  }
  return days.sort((left, right) => left.date.localeCompare(right.date));
}

function formatWeightDate(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}
